/// <amd-module name="Build/Pipelines/Tabs/Pipelines/PipelinesTable" />
import * as React from "react";

import { BuildDefinition, BuildReason, BuildStatus, DefinitionQueueStatus, DefinitionReference } from "Build/Client/Contracts/Build";
import { PipelineData } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { BranchLink } from "Build/Common/Library/Components/BranchLink"
import { ProcessType } from "Build/Common/Library/Constants";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import { getBuildStatusIndicatorData } from "Build/Common/Library/Indicator";
import { Security, BuildRequiredPermissions } from "Build/Common/Library/Security"
import { DefinitionFavoritesActionCreator } from "Build/Pipelines/Actions/Favorites";
import * as PipelineActions from "Build/Pipelines/PipelineActions";
import { PipelinesStore } from "Build/Pipelines/PipelinesStore";
import * as BuildReasonTools from "Build/Pipelines/Tools/BuildReasonTools";
import * as Resources from "Build/Pipelines/Resources";

import { IFavoritesService } from 'Favorites/Common/FavoritesService';

import { getTFSPageData } from "Tfs/Platform/Page";

import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssIdentityService } from "VSS/Platform/Identity";
import { VssComponent, IVssComponentProperties, IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

import { Card } from "VSSUI/Card";
import { TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { Status, StatusSize } from "VSSUI/Status";
import { ColumnMore, IMeasurementStyle, ITableBreakpoint, ITableColumn, SimpleTableCell, Table, TableCell, TableColumnStyle, TwoLineTableCell } from "VSSUI/Table";
import { Button } from "VSSUI/Button";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { VssPersona } from "VSSUI/VssPersona";
import { ZeroData } from "VSSUI/ZeroData";

import { DateAndDuration } from "Components/DateAndDuration";
import { Performance } from "Build/Pipelines/Performance";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

interface ITableRow {
    index: number;
}

export interface IPipelinesTableProps extends IVssComponentProperties {
    store: PipelinesStore;
    security: Security;
    favoritesActionCreator: DefinitionFavoritesActionCreator;
    isPageLoad: boolean;
}

export interface IPipelinesTableState {
    loading: boolean;
    filtered: boolean;
}

export class PipelinesTable extends VssComponent<IPipelinesTableProps, IPipelinesTableState> {
    private _identityService: IVssIdentityService;
    private _ttiMarked = false;

    constructor(props: IPipelinesTableProps, context: IVssComponentContext) {
        super(props, context);

        this._store = props.store;
        this._store.fetchData(!props.isPageLoad);

        // Duplicate array with a splice rather than directly wrapping the store's array
        this._itemProvider = new ObservableArray<PipelineData>()
        this._itemProvider.splice(0, this._itemProvider.length, ...this._store.getPipelines());

        this.state = {
            loading: this._store.isLoadingData(),
            filtered: this._store.isListFiltered()
        }
        this._identityService = this.context.pageContext.getService<IVssIdentityService>("IVssIdentityService");
    }

    public render() {
        let pipelineItemProvider = this._itemProvider;

        if (this.state.loading) {
            return (
                <Card className="flex-grow bolt-card-no-vertical-padding pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                    <Spinner className="pipelines-filter-spinner" size={SpinnerSize.large} label={Resources.Loading} />
                </Card>
            );
        }

        if (pipelineItemProvider.length === 0) {
            const image = getAssetLocation(this.context.pageContext, "ms.vss-build-web/pipelines/Content/no-results.png");
            const text = this.state.filtered ? Resources.NoResultsFound : Resources.ZeroDataNoCreatePipelinesPrimary;
            return (
                <Card className="flex-grow bolt-card-no-vertical-padding pipelines-minimum-card-height" contentProps={{ contentPadding: false }}>
                    <div className="pipelines-zero-data pipelines-whole-page-centered">
                        <ZeroData
                            imageAltText={text}
                            imagePath={image}
                            secondaryText={text}
                        />
                    </div>
                </Card>
            );
        }

        return (
            <Card 
                className="flex-grow bolt-table-card" 
                contentProps={{ contentPadding: false }} 
                titleProps={{ text: this.state.filtered ? Resources.FilteredPipelinesView : Resources.RecentView, size: TitleSize.Medium, ariaLevel: 2 }}>
                <Table ariaLabel={Resources.RecentPipelines} columns={this._tableColumns} itemProvider={pipelineItemProvider} onActivate={this._onActivateRow} tableBreakpoints={this._tableBreakpoints} showHeader={(screenSize) => screenSize >= ScreenSize.small} />
            </Card>
        );
    }

    public componentDidMount() {
        this._store.addListener(this._onStoreChanged);
        // see bug #1532544, this isn't correct since we are using tableBreakpoints, we don't have a suggestion yet for this...
        this._markTTI();
    }

    public componentWillUnmount() {
        this._store.removeListener(this._onStoreChanged);
    }

    public componentDidUpdate() {
        this._markTTI();
    }

    private _markTTI() {
        if (!this._ttiMarked && !this.state.loading) {
            this._ttiMarked = true;
            new Performance(this.context.pageContext).mark("recent");
        }
    }

    private _onStoreChanged = () => {
        this._itemProvider.splice(0, this._itemProvider.length, ...this._store.getPipelines());
        this.setState({
            loading: this._store.isLoadingData(),
            filtered: this._store.isListFiltered()
        });
    }

    private _renderStatusIcon = (tableItem: PipelineData, className?: string): JSX.Element => {
        const status = getBuildStatusIndicatorData(tableItem.latestRun ? tableItem.latestRun.status : BuildStatus.None, tableItem.latestRun ? tableItem.latestRun.result : undefined);
        return (
            <Tooltip text={status.label}>
                <span className="flex-row">
                    <Status ariaLabel={status.statusProps.ariaLabel || status.label} className={className} {...status.statusProps} size={StatusSize.l} />
                </span>
            </Tooltip>
        );
    }

    private _renderPipelineName = (tableItem: PipelineData): JSX.Element => {
        return (
            <span className="inline-flex-row scroll-hidden font-weight-semibold body-m">
                <Tooltip overflowOnly={true} text={tableItem.name}>
                    <span className="text-ellipsis">{tableItem.name}</span>
                </Tooltip>
                {tableItem.isDraft && <sup className="font-size-xxs pipelines-draft-indicator">{Resources.Draft}</sup>}
            </span>
        );
    }

    private _getLastRunText = (tableItem: PipelineData): string => {
        let lastRunText = "";
        if (tableItem.latestRun) {
            lastRunText = formatPipelineRunName(
                tableItem.latestRun.number,
                tableItem.latestRun.sourceVersionMessage,
                tableItem.latestRun.appendCommitMessageToRunName
            );
        }
        return lastRunText;
    };

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        let item;
        if (tableItem.path === "\\") {
            // pipelines at root will still show "\" as their path
            item = <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="scroll-hidden">
                        {this._renderStatusIcon(tableItem, "icon-large-margin")}
                        <div className="flex-row scroll-hidden">
                            {this._renderPipelineName(tableItem)}
                        </div>
                    </SimpleTableCell>;
        } else {
            // pipelines in folder will have their path set as "\path\name"
            // we don't care about the leading "\" so we strip it off
            const path = tableItem.path.substr(1);
            item = <TwoLineTableCell
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                        key={"col-"+columnIndex}
                        className="scroll-hidden"
                        iconProps={{render: () => this._renderStatusIcon(tableItem, "icon-large-margin")}}
                        line1={
                            <div className="flex-row scroll-hidden">
                                {this._renderPipelineName(tableItem)}
                            </div>
                        }
                        line2={
                            <span className="fontSize secondary-text flex-row flex-baseline text-ellipsis pipeline-firefox-fix">
                                <Icon iconName="Folder" className="icon-margin" />
                                <Tooltip overflowOnly={true} text={path}>
                                    <span className="text-ellipsis" key="run-folder-text">
                                        {path}
                                    </span>
                                </Tooltip>
                            </span>
                        }
                    />
        }
        return item;
    }

    private _renderDetailsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        if (tableItem.latestRun) {
            const pageContext = this.context.pageContext;
            const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
            const buildLink = linkingService.getBuildUrl(tableItem.latestRun.id);

            let topLineText = this._getLastRunText(tableItem);

            const topLine = <Link className="bolt-table-link bolt-table-inline-link fontSizeM text-ellipsis" excludeTabStop href={buildLink} onClick={
                (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    event.preventDefault();
                    onClickFPS(pageContext, buildLink, true, event);
                }
            }>
                {topLineText}
            </Link>;

            const tooltip = "";

            let userImage;
            let imageUrl;
            if(tableItem.latestRun.requestedForId) {
                if(userHasClaim(this.context.pageContext, UserClaims.Member)) {
                    imageUrl= this._identityService.getIdentityImageUrl(tableItem.latestRun.requestedForId);
                }
                userImage = <span className="padding-left-4"><VssPersona className="v-align-middle" imageUrl={imageUrl} size="extra-small" /></span>
            }

            var repositoryId = tableItem.latestRun.sourceRepositoryId;
            var repositoryName = tableItem.latestRun.sourceRepositoryName;
            var repositoryUrl = tableItem.latestRun.sourceRepositoryUrl;

            if (isFeatureFlagEnabled(this.context.pageContext, "Build2.SelectTriggeringRepositoriesBasedOnSHA")) {
                if (tableItem.latestRun.reason === BuildReason.PullRequest && 
                    tableItem.latestRun.sourceRepositoryId !== tableItem.latestRun.triggerInfo["pr.triggerRepository"]) {
                        repositoryId = tableItem.latestRun.triggerInfo["pr.triggerRepository"];
                        repositoryName = "";
                        repositoryUrl = "";
                }
                else if (tableItem.latestRun.triggerInfo["ci.triggerRepository"] && 
                    tableItem.latestRun.sourceRepositoryId !== tableItem.latestRun.triggerInfo["ci.triggerRepository"]) {
                        repositoryId = tableItem.latestRun.triggerInfo["ci.triggerRepository"];
                        repositoryName = "";
                        repositoryUrl = "";
                }
            }
            
            const bottomLine = <Tooltip overflowOnly={true} text={tooltip}>
                <span className="fontSize secondary-text flex-row flex-baseline text-ellipsis bolt-table-inline-link-left-padding pipeline-firefox-fix">
                    <Icon iconName={BuildReasonTools.getReasonIcon(tableItem.latestRun.reason)} className="icon-margin" />
                    <span className="text-ellipsis flex-row" key="run-reason-text">
                        {BuildReasonTools.getReasonText(tableItem.latestRun.reason, !!imageUrl)}
                        {userImage}
                    </span>
                    <BranchLink
                        repositoryType={tableItem.latestRun.sourceRepositoryType}
                        repositoryName={repositoryName}
                        repositoryId={repositoryId}
                        repositoryUrl={repositoryUrl}
                        branch={tableItem.latestRun.sourceBranch}
                        monospace={true} />
                </span>
            </Tooltip>;

            return (
                <TwoLineTableCell className="bolt-table-cell-content-with-inline-link no-v-padding" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} line1={topLine} line2={bottomLine} />
            );
        }
        return (
            <TableCell className="bolt-table-cell-content" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div>{Resources.NoRunsYet}</div>
            </TableCell>
        );
    }

    private _renderStatusIconCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                {this._renderStatusIcon(tableItem)}
            </SimpleTableCell>
        );
    }

    private _renderNameWithDetailsCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        const lastRunText = this._getLastRunText(tableItem);
        const lastRun = (
            <Tooltip text={lastRunText}>
                <span className="body-s text-ellipsis secondary-text">{lastRunText}</span>
            </Tooltip>
        );

        return (
            <TwoLineTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} line1={this._renderPipelineName(tableItem)} line2={lastRun} />
        );
    }

    private _renderTimeCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        if (tableItem.latestRun) {
            return <DateAndDuration
                queueTime={tableItem.latestRun.queueTime}
                startTime={tableItem.latestRun.startTime}
                finishTime={tableItem.latestRun.finishTime}
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn} />;
        }

        return (
            <TableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div />
            </TableCell>
        );
    }

    private _renderFavoriteCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<PipelineData>, tableItem: PipelineData): JSX.Element => {
        let cellContents = <span></span>;

        const favoritesService = this.context.pageContext.getService<IFavoritesService>("IFavoritesService");

        let alwaysVisible = false;

        if (favoritesService.canUseFavorites()) {
            if (tableItem.isFavorite) {
                alwaysVisible = true;
                const removeFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const activatedItem = this._store.getPipelines()[rowIndex];
                    this.props.favoritesActionCreator.unfavoriteDefinition({
                        id: activatedItem.id,
                        name: activatedItem.name,
                        uri: activatedItem.uri
                    });
                };

                cellContents = (
                    <div className="right-align flex-row">
                        <Button primary={false} className="favorite-icon-color" subtle={true} iconProps={{ iconName: "FavoriteStarFill" }} ariaLabel={Resources.RemoveFromMyFavorites} onClick={removeFavorite} excludeTabStop={true} />
                    </div>
                );
            }
            else {
                const addFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const activatedItem = this._store.getPipelines()[rowIndex];
                    this.props.favoritesActionCreator.favoriteDefinition({
                        id: activatedItem.id,
                        name: activatedItem.name,
                        uri: activatedItem.uri
                    });
                };

                cellContents = (
                    <div className="right-align flex-row">
                        <Button primary={false} subtle={true} iconProps={{ iconName: "FavoriteStar" }} ariaLabel={Resources.AddToMyFavorites} onClick={addFavorite} excludeTabStop={true} />
                    </div>
                );
            }
        }

        return (
            <TableCell className="bolt-table-cell-side-action pipelines-favorite-cell" columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className={css("flex-column", "padding-right-4", !alwaysVisible && "bolt-table-cell-content-reveal")}>
                    {cellContents}
                </div>
            </TableCell>
        );
    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow) => {
        const pageContext = this.context.pageContext;

        const activatedItem = this._store.getPipelines()[row.index];

        if (activatedItem && activatedItem.linkProps.href) {
            onClickFPS(pageContext, activatedItem.linkProps.href, true, event);
        }
    }

    private _showMenuForItem = (item: PipelineData) => {
        return this.props.security.isMember();
    }

    private _getMenuForItem = (pipeline: PipelineData) => {
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (!tfsPageData || !tfsPageData.project || !this.props.security.isMember()) {
            return {
                id: "pipeline-menu",
                items: []
            };
        }
        const projectId = tfsPageData.project.id;
        const canEditDefinition: boolean = this.props.security.canEditDefinition({ id: pipeline.id, path: pipeline.path } as DefinitionReference);
        const canQueueBuild: boolean = this.props.security.canQueueBuild({ id: pipeline.id, path: pipeline.path } as DefinitionReference);
        const canDeleteDefinition: boolean = this.props.security.hasDefinitionPermission({ id: pipeline.id, path: pipeline.path } as DefinitionReference, BuildRequiredPermissions.DeleteBuildDefinition);

        const pageContext = this.context.pageContext;
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const editLink = linkingService.getDefinitionEditUrl(pipeline.id);
        const editYamlLink = linkingService.getYamlPipelineEditUrl(pipeline.id);

        const isYamlProcess = pipeline.processType === ProcessType.Yaml;
        const isDisabled = pipeline.queueStatus === DefinitionQueueStatus.Disabled;

        const menuItems = [];

        menuItems.push(PipelineActions.GetEditViewMenuItem(pageContext, canEditDefinition, isYamlProcess ? editYamlLink : editLink));

        if (canQueueBuild && !isDisabled) {
            menuItems.push(PipelineActions.GetRunMenuItem(pageContext, projectId, pipeline.id));
        }

        menuItems.push(PipelineActions.GetSecurityMenuItem(pageContext, projectId, pipeline.id));

        if (canEditDefinition) {
            menuItems.push(PipelineActions.GetRenameMoveMenuItem(pageContext, projectId, pipeline.id, (updatedDefinition: BuildDefinition) => {
                this._store.updatePipelineNameAndPath(updatedDefinition.id, updatedDefinition.name, updatedDefinition.path);
            }));
        }

        if (canDeleteDefinition) {
            menuItems.push(PipelineActions.GetDeleteMenuItem(pageContext, projectId, pipeline.id, definition => {
                this._store.removePipeline(definition.id);
                definition.drafts.forEach(draft => this._store.removePipeline(draft.id));
            }));
        }

        return {
            id: "pipeline-menu",
            items: menuItems
        };
    }

    private _onSize(event: MouseEvent, index: number, width: number, column: ITableColumn<PipelineData>) {
        (column.width as ObservableValue<number>).value = width;
    }

    private _tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [0, 0, 38, -240, 120, 2.625, 2.625]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [240, -100, 0, 0, 156, 2.625, 2.625]
        },
        {
            breakpoint: 840,
            columnWidths: [340, -100, 0, 0, 156, 2.625, 2.625]
        }
    ];

    private _tableColumns: ITableColumn<PipelineData>[] = [
        {
            id: "name",
            width: new ObservableValue(340),
            name: Resources.PipelineColumnName,
            className: "pipelines-cell",
            renderCell: this._renderNameCell,
            columnStyle: TableColumnStyle.Primary,
            onSize: this._onSize
        },
        {
            id: "details",
            width: new ObservableValue(-100),
            name: Resources.LastRunColumnName,
            className: "pipelines-cell",
            renderCell: this._renderDetailsCell
        },
        {
            id: "status-icon",
            width: new ObservableValue(38),
            className: "pipelines-cell",
            renderCell: this._renderStatusIconCell,
            ariaLabel: Resources.StatusLabel
        },
        {
            id: "name-with-details",
            width: new ObservableValue(-240),
            className: "pipelines-cell",
            renderCell: this._renderNameWithDetailsCell,
            columnStyle: TableColumnStyle.Primary,
            ariaLabel: Resources.PipelineColumnName
        },
        {
            id: "time",
            width: new ObservableValue(156),
            className: "pipelines-cell",
            renderCell: this._renderTimeCell,
            name: Resources.TimeAndDuration,
            headerTitleClassName: "visually-hidden"
        },
        {
            id: "favorite",
            width: new ObservableValue(2.625),
            widthStyle: IMeasurementStyle.REM,
            className: "pipelines-cell",
            renderCell: this._renderFavoriteCell,
            name: Resources.Favorite,
            headerTitleClassName: "visually-hidden"
        },
        new ColumnMore(this._getMenuForItem, this._showMenuForItem)
    ];

    private _store: PipelinesStore;
    private _itemProvider: IObservableArray<PipelineData>;
}
